import { useState, useEffect, useRef, FC } from 'react';
import { IonBackdrop } from '@ionic/react';
import { AnimatedLogo } from '../../AnimatedLogo';
import { ALLIE_LOADING_EVENTS } from './LogoLoading.constant';
import { AllieLogoLoadingProps, LoadingState } from './LogoLoading.types';
import { useAllieLogoLoading } from './useAllieLogoLoading';

export const AllieLogoLoading: FC<AllieLogoLoadingProps> = ({
  config,
  routeConfig,
  backgroundColor,
  logoSize,
  logoStyle,
}) => {
  const [loadingState, setLoadingState] = useState<LoadingState>({
    isVisible: true, // Debe ser visible para que el componente se monte
    isHiding: false,
    showLogo: false, // No mostrar el logo inicialmente para evitar el flash
  });

  const hideTimerRef = useRef<NodeJS.Timeout | null>(null);
  const safetyTimerRef = useRef<NodeJS.Timeout | null>(null);
  const { config: loadingConfig } = useAllieLogoLoading({
    routeConfig,
    config,
  });

  useEffect(() => {
    return () => {
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
      }
      if (safetyTimerRef.current) {
        clearTimeout(safetyTimerRef.current);
      }
    };
  }, []);

  const hideLoading = () => {
    if (!loadingState.isVisible || loadingState.isHiding) return;

    if (safetyTimerRef.current) {
      clearTimeout(safetyTimerRef.current);
      safetyTimerRef.current = null;
    }

    setLoadingState((prev) => ({
      ...prev,
      showLogo: false,
      isHiding: true,
    }));

    hideTimerRef.current = setTimeout(() => {
      setLoadingState((prev) => ({
        ...prev,
        isVisible: false,
      }));
    }, 1000);
  };

  const showLoading = () => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }

    if (safetyTimerRef.current) {
      clearTimeout(safetyTimerRef.current);
      safetyTimerRef.current = null;
    }

    setLoadingState({
      isVisible: true,
      isHiding: false,
      showLogo: true,
    });

    safetyTimerRef.current = setTimeout(() => {
      hideLoading();
    }, loadingConfig.safetyTime);

    if (loadingConfig.autoHide) {
      const hideTime = loadingConfig.autoHideTime;
      setTimeout(hideLoading, hideTime);
    }
  };

  useEffect(() => {
    const handleHideEvent = () => hideLoading();
    const handleShowEvent = () => showLoading();

    window.addEventListener(ALLIE_LOADING_EVENTS.HIDE_LOADING, handleHideEvent);
    window.addEventListener(ALLIE_LOADING_EVENTS.SHOW_LOADING, handleShowEvent);

    return () => {
      window.removeEventListener(ALLIE_LOADING_EVENTS.HIDE_LOADING, handleHideEvent);
      window.removeEventListener(ALLIE_LOADING_EVENTS.SHOW_LOADING, handleShowEvent);
    };
  }, [loadingState.isVisible, loadingState.isHiding]);

  useEffect(() => {
    // Pequeño delay para asegurar que el DOM esté completamente renderizado
    const timer = setTimeout(() => {
      showLoading();
    }, loadingConfig.delayTime || 0);

    return () => clearTimeout(timer);
  }, []);

  if (!loadingState.isVisible) {
    return null;
  }

  return (
    <>
      <IonBackdrop
        visible={loadingState.showLogo}
        style={{
          backgroundColor: backgroundColor ? `var(--allie-color-${backgroundColor})` : 'white',
          opacity: loadingState.isHiding ? 0 : 1,
          transition: 'opacity 1000ms ease-out',
        }}
      />
      {loadingState.showLogo && (
        <AnimatedLogo
          size={logoSize}
          duration={1.5}
          repeatDelay={0}
          animateScale={true}
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 1000,
            ...logoStyle,
          }}
        />
      )}
    </>
  );
};
